import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Clock, Play, Calendar, User, MapPin, Search, LayoutGrid, List
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useApp } from '../context/AppContext';
import { useTrace } from '../context/TraceContext';
import TraceLabel from '../components/trace/TraceLabel';
import ReportExportButton from '../components/export/ReportExportButton';
import ReportSection from '../components/export/ReportSection';
import { tasks, areas, formatDate } from '../data/mockData';

const TasksPage = () => {
  const navigate = useNavigate();
  const { isReadOnly } = useApp();
  const { pidTraceMode } = useTrace();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [criticalityFilter, setCriticalityFilter] = useState('all');
  const [viewMode, setViewMode] = useState('table');

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'In Progress': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Pending': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Overdue': return 'bg-red-50 text-red-700 border-red-200';
      case 'Upcoming': return 'bg-slate-100 text-slate-600 border-slate-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getCriticalityColor = (criticality) => {
    switch (criticality) {
      case 'Critical': return 'bg-red-50 text-red-700 border-red-200';
      case 'High': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Medium': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Low': return 'bg-slate-100 text-slate-600 border-slate-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getAreaName = (areaId) => {
    const area = areas.find(a => a.id === areaId);
    return area ? area.name : areaId;
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         task.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    const matchesCriticality = criticalityFilter === 'all' || task.criticality === criticalityFilter;
    return matchesSearch && matchesStatus && matchesCriticality;
  });

  const tasksByStatus = {
    today: filteredTasks.filter(t => t.status === 'Pending' || t.status === 'In Progress'),
    upcoming: filteredTasks.filter(t => t.status === 'Upcoming'),
    overdue: filteredTasks.filter(t => t.status === 'Overdue'),
    completed: filteredTasks.filter(t => t.status === 'Completed')
  };

  const TaskCard = ({ task }) => (
    <Card 
      className={`bg-white border-slate-200 hover:border-slate-300 hover:shadow-md transition-all cursor-pointer group relative ${pidTraceMode ? 'border-cyan-300' : ''}`}
      onClick={() => navigate(`/tasks/${task.id}`)}
    >
      <TraceLabel prd="PRD §6.3" req="SM-DI-001" />
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <Badge className={`${getStatusColor(task.status)} text-xs`}>{task.status}</Badge>
          <Badge className={`${getCriticalityColor(task.criticality)} text-xs`}>{task.criticality}</Badge>
        </div>
        <h3 className="text-sm font-medium text-slate-700 mb-2 line-clamp-2">{task.name}</h3>
        <div className="space-y-1.5 text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <MapPin className="h-3 w-3" />
            <span>{getAreaName(task.area)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-3 w-3" />
            <span>{formatDate(task.dueDate)}</span>
          </div>
          <div className="flex items-center gap-2">
            <User className="h-3 w-3" />
            <span>{task.owner}</span>
          </div>
        </div>
        {!isReadOnly && (task.status === 'Pending' || task.status === 'In Progress') && (
          <Button 
            size="sm" 
            className="w-full mt-3 bg-[#fd4816] hover:bg-[#ea380c] text-white"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/tasks/${task.id}/execute`);
            }}
            data-testid={`start-task-${task.id}`}
          >
            <Play className="h-3 w-3 mr-2" />
            Start (iPad Mode)
          </Button>
        )}
      </CardContent>
    </Card>
  );

  const TaskRow = ({ task }) => (
    <TableRow 
      className="cursor-pointer hover:bg-slate-50"
      onClick={() => navigate(`/tasks/${task.id}`)}
      data-testid={`task-row-${task.id}`}
    >
      <TableCell className="font-mono text-xs text-slate-500">{task.id}</TableCell>
      <TableCell>
        <div className="max-w-md">
          <p className="text-sm text-slate-700 truncate font-medium">{task.name}</p>
          <p className="text-xs text-slate-500">{getAreaName(task.area)}</p>
        </div>
      </TableCell>
      <TableCell>
        <Badge className={`${getStatusColor(task.status)} text-xs`}>{task.status}</Badge>
      </TableCell>
      <TableCell>
        <Badge className={`${getCriticalityColor(task.criticality)} text-xs`}>{task.criticality}</Badge>
      </TableCell>
      <TableCell className="text-sm text-slate-600">{task.frequency}</TableCell>
      <TableCell className="text-sm text-slate-600">{formatDate(task.dueDate)}</TableCell>
      <TableCell className="text-sm text-slate-600">{task.owner}</TableCell>
      <TableCell>
        {!isReadOnly && (task.status === 'Pending' || task.status === 'In Progress') && (
          <Button 
            size="sm" 
            className="bg-[#fd4816] hover:bg-[#ea380c] text-white"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/tasks/${task.id}/execute`);
            }}
          >
            <Play className="h-3 w-3 mr-1" />
            Start
          </Button>
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <div className="space-y-6" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Tasks
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage and execute GMP tasks and checklists
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* <ReportExportButton
            title="Tasks"
            subtitle="GMP tasks and checklist executions"
          /> */}
          <Badge className="bg-amber-50 text-amber-700 border border-amber-200">
            {tasksByStatus.overdue.length} Overdue
          </Badge>
          <Badge className="bg-blue-50 text-blue-700 border border-blue-200">
            {tasksByStatus.today.length} Today
          </Badge>
          {isReadOnly && (
            <Badge className="bg-amber-50 text-amber-700 border border-amber-200 font-semibold">
              READ-ONLY
            </Badge>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className={`bg-white border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
        <TraceLabel prd="PRD §6.3" req="SM-DOC-001" />
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white border-slate-200 text-slate-700"
                data-testid="task-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px] bg-white border-slate-200 text-slate-700" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="all" className="text-slate-700">All Status</SelectItem>
                <SelectItem value="Pending" className="text-slate-700">Pending</SelectItem>
                <SelectItem value="In Progress" className="text-slate-700">In Progress</SelectItem>
                <SelectItem value="Completed" className="text-slate-700">Completed</SelectItem>
                <SelectItem value="Overdue" className="text-slate-700">Overdue</SelectItem>
                <SelectItem value="Upcoming" className="text-slate-700">Upcoming</SelectItem>
              </SelectContent>
            </Select>
            <Select value={criticalityFilter} onValueChange={setCriticalityFilter}>
              <SelectTrigger className="w-[150px] bg-white border-slate-200 text-slate-700" data-testid="criticality-filter">
                <SelectValue placeholder="Criticality" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="all" className="text-slate-700">All Criticality</SelectItem>
                <SelectItem value="Critical" className="text-slate-700">Critical</SelectItem>
                <SelectItem value="High" className="text-slate-700">High</SelectItem>
                <SelectItem value="Medium" className="text-slate-700">Medium</SelectItem>
                <SelectItem value="Low" className="text-slate-700">Low</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex border border-slate-200 rounded-md overflow-hidden">
              <Button
                variant="ghost"
                size="sm"
                className={`rounded-none ${viewMode === 'table' ? 'bg-slate-100' : ''} hover:bg-slate-50`}
                onClick={() => setViewMode('table')}
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`rounded-none ${viewMode === 'grid' ? 'bg-slate-100' : ''} hover:bg-slate-50`}
                onClick={() => setViewMode('grid')}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tasks Tabs */}
      <Tabs defaultValue="today" className="w-full">
        <TabsList className="bg-slate-100 border border-slate-200">
          <TabsTrigger value="today" className="data-[state=active]:bg-[#fd4816] data-[state=active]:text-white">
            Today ({tasksByStatus.today.length})
          </TabsTrigger>
          <TabsTrigger value="upcoming" className="data-[state=active]:bg-[#fd4816] data-[state=active]:text-white">
            Upcoming ({tasksByStatus.upcoming.length})
          </TabsTrigger>
          <TabsTrigger value="overdue" className="data-[state=active]:bg-[#fd4816] data-[state=active]:text-white">
            Overdue ({tasksByStatus.overdue.length})
          </TabsTrigger>
          <TabsTrigger value="completed" className="data-[state=active]:bg-[#fd4816] data-[state=active]:text-white">
            Completed ({tasksByStatus.completed.length})
          </TabsTrigger>
        </TabsList>

        {['today', 'upcoming', 'overdue', 'completed'].map(tab => (
          <TabsContent key={tab} value={tab} className="mt-4">
            <ReportSection
              id={`tasks-${tab}`}
              title={`Tasks - ${tab.charAt(0).toUpperCase() + tab.slice(1)}`}
              description={`${tab.charAt(0).toUpperCase() + tab.slice(1)} tasks list`}
            >
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {tasksByStatus[tab].map(task => (
                    <TaskCard key={task.id} task={task} />
                  ))}
                  {tasksByStatus[tab].length === 0 && (
                    <div className="col-span-full text-center py-12 text-slate-500">
                      No tasks found
                    </div>
                  )}
                </div>
              ) : (
                <Card className={`bg-white border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
                  <TraceLabel prd="PRD §6.3" req="SM-DI-001" />
                  <Table>
                  <TableHeader>
                    <TableRow className="border-slate-200 hover:bg-transparent">
                      <TableHead className="text-slate-600">ID</TableHead>
                      <TableHead className="text-slate-600">Task</TableHead>
                      <TableHead className="text-slate-600">Status</TableHead>
                      <TableHead className="text-slate-600">Criticality</TableHead>
                      <TableHead className="text-slate-600">Frequency</TableHead>
                      <TableHead className="text-slate-600">Due Date</TableHead>
                      <TableHead className="text-slate-600">Owner</TableHead>
                      <TableHead className="text-slate-600 w-24">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tasksByStatus[tab].map(task => (
                      <TaskRow key={task.id} task={task} />
                    ))}
                    {tasksByStatus[tab].length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                          No tasks found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                </Card>
              )}
            </ReportSection>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default TasksPage;
